import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Star, TrendingUp, TriangleAlert } from 'lucide-react';

import { getTrainerFeedbackStats } from '../../../services/StudentFeedbackService';
import {
  MIN_RESPONSES,
  AT_RISK_AVERAGE,
  AT_RISK_LOW_SHARE,
  bandFor,
} from '../Feedback/feedbackBands';
import BandDonut from '../Feedback/BandDonut';
import { Skeleton, SkeletonGroup } from '../common/Skeleton/Skeleton.jsx';
import { silenceFor } from '../common/Loader/busy';
/* .ba and .ba-head -- the frame shared with the attendance report.
   Owned by this component rather than assumed from the page. */
import './BusinessPulse.css';
import './trainer-feedback-report.css';

/**
 * How the teaching is going, by trainer.
 *
 * The chart is a headcount and nothing else: how many trainers are
 * doing well, how many need looking at. Not a bar per trainer — that
 * is a ranked list drawn sideways, and it answers "who" twice while
 * never answering "how bad is this overall".
 *
 * No student appears here, named or quoted. What any one student said
 * is on the feedback page, which is where reading it belongs.
 *
 * Aggregated by the API (/studentfeedbacks/trainer-stats) rather than
 * here: the browser would have to page the whole feedback table to
 * compute an average, and this box wants a handful of counts.
 */

/* The thresholds live in Feedback/feedbackBands.js, shared with the
   trainer's own summary box. If each owned a copy, an admin could be
   told a trainer is at risk while that trainer's dashboard says they
   are fine. */
const HOW_MANY = 3;

/* MEANING, so literal — the same tones the verdict strips use. A
   palette able to render "needs looking at" in the same colour as
   "doing well" would leave this box saying nothing.

   Colour is not the only encoding: every slice is named and counted
   in the key beside the ring. */
const BAND_TONE = {
  good: '#0E7C6B',
  steady: '#1568A6',
  risk: '#C0392B',
  quiet: '#C2CAD8',
};

const Score = ({ value }) => (
  <span className="tfr-score">
    <Star size={13} aria-hidden="true" />
    <b>{Number(value || 0).toFixed(1)}</b>
  </span>
);

const TrainerRow = ({ row, tone }) => (
  <li className={`tfr-row tfr-row--${tone}`}>
    <span className="tfr-row-id">
      <span className="tfr-row-name">{row.trainerName}</span>
      <span className="tfr-row-sub">
        {row.responses} {row.responses === 1 ? 'response' : 'responses'}
        {row.lowCount > 0 && ` · ${row.lowCount} rated 2 or below`}
      </span>
    </span>
    <Score value={row.averageRating} />
  </li>
);

const TrainerFeedbackReport = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    // The box shows its own skeleton, so the global overlay stays down.
    silenceFor();
    getTrainerFeedbackStats()
      .then((data) => {
        if (!cancelled) setRows(Array.isArray(data) ? data : []);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const { doingWell, atRisk, segments, headcount, rated, average } = useMemo(() => {
    /* Every trainer with any feedback at all falls in exactly one band,
       so the ring adds up to the headcount and the reader can trust the
       slices to be the whole picture. 'quiet' is its own band, not a
       silent drop: too few answers to judge is a different thing from
       judged and fine. */
    const banded = rows.map((r) => ({
      ...r,
      band: bandFor({
        averageRating: r.averageRating,
        lowCount: r.lowCount,
        responses: r.responses,
      }),
    }));

    const count = (band) => banded.filter((r) => r.band === band).length;

    /* Only trainers with enough responses to mean anything reach the
       shortlists. One student having a bad week is not a trainer at
       risk, and putting them at the top of a "needs looking at" list is
       how a report stops being trusted. */
    const solid = banded.filter((r) => r.responses >= MIN_RESPONSES);

    const risk = solid
      .filter((r) => r.band === 'risk')
      .sort((a, b) => a.averageRating - b.averageRating)
      .slice(0, HOW_MANY);

    const riskIds = new Set(risk.map((r) => r.trainerId));
    const well = solid
      .filter((r) => !riskIds.has(r.trainerId))
      .sort((a, b) => b.averageRating - a.averageRating)
      .slice(0, HOW_MANY);

    const totalResponses = rows.reduce((n, r) => n + r.responses, 0);
    const weighted = rows.reduce((n, r) => n + r.averageRating * r.responses, 0);

    return {
      doingWell: well,
      atRisk: risk,
      segments: [
        { label: 'Doing well', value: count('good'), color: BAND_TONE.good },
        { label: 'Steady', value: count('steady'), color: BAND_TONE.steady },
        { label: 'Needs looking at', value: count('risk'), color: BAND_TONE.risk },
        { label: 'Not enough yet', value: count('quiet'), color: BAND_TONE.quiet },
      ],
      headcount: banded.length,
      rated: solid.length,
      average: totalResponses ? weighted / totalResponses : 0,
    };
  }, [rows]);

  return (
    <section className="ba tfr">
      <header className="ba-head">
        <div>
          <h2 className="ba-title">Teaching, by trainer</h2>
          <p className="ba-sub">
            {loading
              ? 'Reading the feedback…'
              : rated === 0
                ? 'Not enough feedback yet to compare trainers.'
                : `${rated} ${rated === 1 ? 'trainer' : 'trainers'} with ${MIN_RESPONSES}+ responses · ${average.toFixed(1)} average overall`}
          </p>
        </div>

        <button
          type="button"
          className="td-head-action"
          onClick={() => navigate('/session-feedback')}
        >
          Show more
        </button>
      </header>

      {loading ? (
        /* The ring and the two shortlists, shimmering, until the counts
           arrive -- an empty ring meanwhile read as "nobody rated". */
        <SkeletonGroup label="Loading trainer feedback" className="tfr-grid">
          <div className="tfr-chart lms-skel-stack">
            <Skeleton width="40" />
            <Skeleton shape="ring" />
          </div>
          <div className="tfr-cols">
            {[0, 1].map((col) => (
              <div className="tfr-col lms-skel-stack" key={col}>
                <Skeleton width="55" />
                <Skeleton shape="row" />
                <Skeleton shape="row" />
                <Skeleton shape="row" />
              </div>
            ))}
          </div>
        </SkeletonGroup>
      ) : headcount === 0 ? (
        <p className="tfr-empty">
          Trainers appear here once their students have rated at least{' '}
          {MIN_RESPONSES} sessions each.
        </p>
      ) : (
        /* Ring and shortlists side by side on a desktop: the box is far
           wider than either needs, and a chart run to the full width of
           it pushes the names worth acting on below the fold. */
        <div className="tfr-grid">
          <div className="tfr-chart">
            <h3 className="tfr-col-head">How the team is doing</h3>
            <BandDonut
              segments={segments}
              centreValue={headcount}
              centreLabel={headcount === 1 ? 'trainer' : 'trainers'}
              empty="No trainer has been rated yet."
            />
          </div>

          <div className="tfr-cols">
            <div className="tfr-col">
              <h3 className="tfr-col-head tfr-col-head--good">
                <TrendingUp size={14} aria-hidden="true" />
                Doing well
              </h3>
              {doingWell.length > 0 ? (
                <ul className="tfr-list">
                  {doingWell.map((r) => (
                    <TrainerRow key={r.trainerId} row={r} tone="good" />
                  ))}
                </ul>
              ) : (
                <p className="tfr-empty">Nobody clear of the risk band yet.</p>
              )}
            </div>

            <div className="tfr-col">
              <h3 className="tfr-col-head tfr-col-head--risk">
                <TriangleAlert size={14} aria-hidden="true" />
                Needs looking at
              </h3>
              {atRisk.length > 0 ? (
                <ul className="tfr-list">
                  {atRisk.map((r) => (
                    <TrainerRow key={r.trainerId} row={r} tone="risk" />
                  ))}
                </ul>
              ) : (
                /* Said plainly. An empty column with a dash in it reads as
                   a report that failed to load. */
                <p className="tfr-empty">
                  No trainer is below {AT_RISK_AVERAGE} or carrying{' '}
                  {AT_RISK_LOW_SHARE}% poor ratings.
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default TrainerFeedbackReport;
